import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as Avatar from '@givebutter/primitives/avatar';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { faPaperPlane, faPenToSquare, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAccountMembersWithInvites } from 'api/account';
import * as DeleteMemberDialog from 'assets/js/pages/account-settings/users/DeleteMemberDialog';
import * as ResendMemberInviteDialog from 'assets/js/pages/account-settings/users/ResendMemberInviteDialog';
import { checkIfMe } from 'assets/js/pages/account-settings/users/utils';
import { CopyButton } from 'components/copy-button';
import * as DropdownMenu from 'components/dropdown-menu';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMemberOrInvite, AccountMemberRoleInfo } from 'types/domain';

const columnHelper = createColumnHelper<AccountMemberOrInvite>();

const AccountMembersV2 = () => {
    const { accountId, access } = useCurrentAccount();
    const { pagination, setPagination } = Table.useReactTableState();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const { filters } = useFiltersByKey('accountMembers');

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
            filters,
        },
        500,
    );

    const { data: members } = useSuspenseQuery({
        queryKey: accountQueryKeys.membersWithInvitesWithFilters(accountId, params),
        queryFn: () => fetchAccountMembersWithInvites(accountId, params),
        refetchOnMount: 'always',
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: 'Contact',
                cell: info => {
                    const member = info.row.original;

                    return (
                        <div className="flex items-center gap-2">
                            <Avatar.Root size="xs">
                                <Avatar.Image
                                    alt={`Profile picture of ${member.first_name} ${member.last_name}`}
                                    src={member.picture}
                                />
                                <Avatar.Fallback aria-label={member.first_name ?? member.email}>
                                    {member.first_name ? member.first_name[0] : member.email[0]}
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <div className="flex flex-col">
                                <p className="font-medium">
                                    {member.first_name} {member.last_name}
                                </p>
                                <div className="flex items-center gap-1">
                                    <a href={`mailto:${member.email}`} className="text-sm">
                                        {member.email}
                                    </a>
                                    <CopyButton text={member.email} />
                                </div>
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('roles', {
                header: 'Role',
                cell: info => {
                    const member = info.row.original;
                    const memberRolesInfo = member.roles_info;

                    if (!memberRolesInfo) {
                        return <EmptyData />;
                    }

                    return (
                        <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                                {memberRolesInfo.map(
                                    (roleInfo: AccountMemberRoleInfo, i: number) => (
                                        <div key={roleInfo.id} className="flex items-center">
                                            {roleInfo.description ? (
                                                <Tooltip.Root>
                                                    <Tooltip.Trigger
                                                        tabIndex={0}
                                                        className={`cursor-help`}
                                                    >
                                                        {roleInfo.name}
                                                    </Tooltip.Trigger>
                                                    <Tooltip.Portal>
                                                        <Tooltip.Content inverse>
                                                            <p>{roleInfo.description}</p>
                                                            <Tooltip.Arrow inverse />
                                                        </Tooltip.Content>
                                                    </Tooltip.Portal>
                                                </Tooltip.Root>
                                            ) : (
                                                <p>{roleInfo.name}</p>
                                            )}
                                            {i < memberRolesInfo.length - 1 && <span>,</span>}
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('morph_type', {
                header: '',
                cell: info => {
                    const member = info.row.original;
                    return <MemberBadges member={member} />;
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const member = info.row.original;
                    const isMe = checkIfMe(member, access);
                    const isMemberInvite = member.morph_type === 'invite';

                    return (
                        <DropdownMenu.Root>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                <div className="flex flex-col">
                                    {isMemberInvite && (
                                        <>
                                            <ResendMemberInviteDialog.Root member={member}>
                                                <ResendMemberInviteDialog.Trigger>
                                                    <DropdownMenu.Item
                                                        onSelect={e => e.preventDefault()}
                                                    >
                                                        <div className="flex gap-2">
                                                            <FontAwesomeIcon icon={faPaperPlane} />
                                                            <p>Resend invite</p>
                                                        </div>
                                                    </DropdownMenu.Item>
                                                </ResendMemberInviteDialog.Trigger>
                                            </ResendMemberInviteDialog.Root>
                                            <DropdownMenu.LinkItem
                                                to={`/accounts/${accountId}/settings/users/invites/${member.invite_id}/edit`}
                                                disabled={isMe}
                                            >
                                                <div className="flex gap-2">
                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                    <p>Edit invite</p>
                                                </div>
                                            </DropdownMenu.LinkItem>
                                        </>
                                    )}
                                    {member.morph_type === 'account_member' && (
                                        <DropdownMenu.LinkItem
                                            to={`/accounts/${accountId}/settings/users/${member.user_id}/edit`}
                                            disabled={isMe}
                                        >
                                            <div className="flex gap-2">
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                <p>Edit</p>
                                            </div>
                                        </DropdownMenu.LinkItem>
                                    )}
                                    <DeleteMemberDialog.Root member={member}>
                                        <DeleteMemberDialog.Trigger disabled={isMe}>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                disabled={isMe}
                                            >
                                                <div className="flex gap-2">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                    <p>
                                                        {isMemberInvite
                                                            ? 'Delete invite'
                                                            : 'Delete'}
                                                    </p>
                                                </div>
                                            </DropdownMenu.Item>
                                        </DeleteMemberDialog.Trigger>
                                    </DeleteMemberDialog.Root>
                                </div>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [accountId, access],
    );

    const table = useReactTable({
        data: members.data,
        columns,
        pageCount: members.meta.last_page,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, members);

    return (
        <FilterProvider cacheKey={'accountMembers'} resource={'accountMembers'}>
            <Box className="flex flex-col overflow-hidden">
                <div className={'flex justify-between gap-4 border-b border-b-gray-100 p-4'}>
                    <div className={'flex flex-wrap gap-1.5'}>
                        <Label className="sr-only" htmlFor="account_members_search">
                            Search
                        </Label>
                        <Input.Root size={'sm'}>
                            <Input.SearchIcon />
                            <Input.Form
                                id="account_members_search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={'Search'}
                            />
                        </Input.Root>
                    </div>
                </div>
                <Table.Root
                    isLoading={isPending}
                    variant="bordered"
                    table={table}
                    searchTerm={debouncedSearch}
                    isFiltered={!isEmpty(filters)}
                >
                    <div className={'flex flex-wrap items-center gap-2 px-4 py-3'}>
                        <FilterGroupProvider
                            showInnerOperator={false}
                            showOuterOperator={false}
                            defaultOperator="and"
                        >
                            <QuickFiltersBar quickFilters={['roles', 'pendingInvitation']} />
                        </FilterGroupProvider>
                    </div>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>No account members found</Table.EmptyTitle>
                    </Table.Empty>
                    <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </Table.Root>
            </Box>
        </FilterProvider>
    );
};

const MemberBadges = (props: { member: AccountMemberOrInvite }) => {
    const { member } = props;
    const { access } = useCurrentAccount();
    const isMe = checkIfMe(member, access);

    return (
        <div className="flex items-center justify-between gap-2">
            {member.morph_type === 'invite' && (
                <Badge key="invite" color="error">
                    Pending invite
                </Badge>
            )}
            {member.is_expired && (
                <Badge key="expired" color="primary">
                    Expired invite
                </Badge>
            )}
            {isMe && (
                <Badge key="you" color="success">
                    You
                </Badge>
            )}
            {member.owner && (
                <Badge key="owner" color="primary">
                    Owner
                </Badge>
            )}
        </div>
    );
};

export { AccountMembersV2 };
